import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '@/store';
import { getMinutesUntilExpiry } from '@/utils/jwt';
import { performSessionLogout, SESSION_TOAST, tryRefreshSession } from '@/utils/sessionManager';

const WARN_MINUTES = 5;
const REFRESH_MINUTES = 2;
const SILENT_REFRESH_MINUTES = 8;
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = ['mousemove', 'keydown', 'touchstart', 'scroll', 'click'];

/**
 * JWT-aware session lifecycle:
 * - Warn before access token expiry
 * - Proactively refresh while the user is active
 * - Clean logout if refresh fails
 */
export function useTokenLifecycle(): void {
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const warningShownRef = useRef(false);
  const refreshAttemptedRef = useRef(false);
  const silentRefreshAttemptedRef = useRef(false);

  const attemptSilentRefresh = useCallback(() => {
    if (silentRefreshAttemptedRef.current || refreshAttemptedRef.current) return;
    silentRefreshAttemptedRef.current = true;
    void tryRefreshSession(dispatch, { silent: true }).then((ok) => {
      silentRefreshAttemptedRef.current = false;
      if (ok) {
        warningShownRef.current = false;
        refreshAttemptedRef.current = false;
      }
    });
  }, [dispatch]);

  const scheduleCheck = useCallback(() => {
    if (!isAuthenticated || !accessToken) return;

    const minutesLeft = getMinutesUntilExpiry(accessToken);
    if (minutesLeft == null) return;

    if (minutesLeft <= 0) {
      void performSessionLogout(dispatch, 'expired');
      return;
    }

    if (minutesLeft <= SILENT_REFRESH_MINUTES && document.visibilityState === 'visible') {
      attemptSilentRefresh();
    }

    if (minutesLeft <= WARN_MINUTES && !warningShownRef.current) {
      warningShownRef.current = true;
      toast.warn(
        ({ closeToast }) => (
          <div className="text-sm leading-snug">
            <div className="font-semibold">Session expiring soon</div>
            <div className="mt-1 text-white/90">
              You will be signed out in about {minutesLeft} minute{minutesLeft === 1 ? '' : 's'} unless you stay signed in.
            </div>
            <button
              type="button"
              className="mt-2 rounded-md bg-white/15 px-2.5 py-1 text-xs font-semibold hover:bg-white/25"
              onClick={() => {
                closeToast?.();
                void tryRefreshSession(dispatch).then((ok) => {
                  if (!ok) void performSessionLogout(dispatch, 'expired');
                  else {
                    warningShownRef.current = false;
                    refreshAttemptedRef.current = false;
                  }
                });
              }}
            >
              Stay signed in
            </button>
          </div>
        ),
        { toastId: SESSION_TOAST.warn, autoClose: false, closeOnClick: false },
      );
    }

    if (minutesLeft <= REFRESH_MINUTES && !refreshAttemptedRef.current && document.visibilityState === 'visible') {
      refreshAttemptedRef.current = true;
      void tryRefreshSession(dispatch, { silent: true }).then((ok) => {
        if (!ok) void performSessionLogout(dispatch, 'expired');
        else {
          warningShownRef.current = false;
          refreshAttemptedRef.current = false;
        }
      });
    }
  }, [accessToken, attemptSilentRefresh, dispatch, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      warningShownRef.current = false;
      refreshAttemptedRef.current = false;
      silentRefreshAttemptedRef.current = false;
      toast.dismiss(SESSION_TOAST.warn);
      return undefined;
    }

    warningShownRef.current = false;
    refreshAttemptedRef.current = false;
    silentRefreshAttemptedRef.current = false;
    scheduleCheck();

    const onActivity = () => {
      if (!isAuthenticated || !accessToken) return;
      const minutesLeft = getMinutesUntilExpiry(accessToken);
      if (minutesLeft != null && minutesLeft <= SILENT_REFRESH_MINUTES) {
        attemptSilentRefresh();
      }
    };

    const intervalId = window.setInterval(scheduleCheck, 30_000);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') scheduleCheck();
    };
    document.addEventListener('visibilitychange', onVisibility);
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
    };
  }, [accessToken, attemptSilentRefresh, isAuthenticated, scheduleCheck]);
}
